import React from "react";
import Logo from "./Logo";
import Button from '@mui/material/Button';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Tooltip from '@mui/material/Tooltip';
import InputBase from '@mui/material/InputBase';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import CircularProgress from '@mui/material/CircularProgress';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import { useTheme, alpha } from '@mui/material/styles';
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import apiClient from '../api/client';
import { clearToken } from '../auth';

const Header = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { pathname } = useLocation();
  const [headerQuery, setHeaderQuery] = React.useState('');
  const [mobileAnchorEl, setMobileAnchorEl] = React.useState(null);
  const [opsAnchorEl, setOpsAnchorEl] = React.useState(null);
  const [anchorElUser, setAnchorElUser] = React.useState(null);
  const [isGeneratingFile, setIsGeneratingFile] = React.useState(false);
  const [paymentErrorOpen, setPaymentErrorOpen] = React.useState(false);
  const [downloadSuccess, setDownloadSuccess] = React.useState(false);

  const handleOpenOpsMenu = (event) => {
    setOpsAnchorEl(event.currentTarget);
  };
  const handleCloseOpsMenu = () => {
    setOpsAnchorEl(null);
  };

  const handleCloseMobileMenu = () => {
    setMobileAnchorEl(null);
  };

  const handleHeaderSearch = (event) => {
    event.preventDefault();
    const query = headerQuery.trim();
    if (!query) return;
    navigate('/catalog?q=' + encodeURIComponent(query));
  };

  const opsActive = ['/addsku', '/dmdmfg', '/stmfg'].includes(pathname);

  const handleOpenUserMenu = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleUserAction = (setting) => {
    handleCloseUserMenu();
    if (setting === 'Logout') {
      clearToken();
      navigate('/login', { replace: true });
    }
  };

  const handleDownloadStockFile = async () => {
    setIsGeneratingFile(true);
    try {
      const response = await apiClient.get('/downloadinventory', {
        responseType: 'blob',
      });

      const disposition = response.headers['content-disposition'];
      let fileName = 'inventory.csv';
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
      setDownloadSuccess(true);
    } catch (error) {
      console.error('Failed to download stock file:', error);
      if (error?.response?.status === 503) {
        setPaymentErrorOpen(true);
      }
    } finally {
      setIsGeneratingFile(false);
    }
  };

  const navLinkStyle = ({ isActive }) => ({
    textDecoration: 'none',
    color: isActive ? theme.palette.primary.light : theme.palette.text.secondary,
    backgroundColor: isActive ? alpha(theme.palette.primary.main, 0.15) : 'transparent',
    fontWeight: isActive ? 600 : 500,
    fontSize: '0.875rem',
    borderRadius: '8px',
    padding: '7px 14px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    transition: 'all 0.15s ease-in-out',
  });

  return (
    <>
      <AppBar position="sticky" sx={{ zIndex: 1100 }}>
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ minHeight: '64px', display: 'flex', justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <IconButton
                aria-label="Open navigation menu"
                onClick={(event) => setMobileAnchorEl(event.currentTarget)}
                sx={{ display: { xs: 'inline-flex', md: 'none' }, color: 'text.secondary' }}
              >
                <MenuIcon />
              </IconButton>
              <Menu
                id="menu-mobile-nav"
                anchorEl={mobileAnchorEl}
                open={Boolean(mobileAnchorEl)}
                onClose={handleCloseMobileMenu}
                sx={{ mt: 1, '& .MuiPaper-root': { minWidth: 200 } }}
              >
                {[
                  ['/dashboard', 'Dashboard'],
                  ['/catalog', 'Catalog'],
                  ['/addsku', 'Add SKU'],
                  ['/dmdmfg', 'DMD MFGs'],
                  ['/stmfg', 'Stevens MFGs'],
                ].map(([to, label]) => (
                  <MenuItem
                    key={to}
                    component={NavLink}
                    to={to}
                    onClick={handleCloseMobileMenu}
                    selected={pathname === to}
                    sx={{ py: 1.25, fontSize: '0.875rem' }}
                  >
                    {label}
                  </MenuItem>
                ))}
              </Menu>
              <Logo />

              {/* Main Navigation */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1 }}>
                <NavLink to="/dashboard" style={navLinkStyle}>
                  <DashboardOutlinedIcon fontSize="small" />
                  Dashboard
                </NavLink>

                <NavLink to="/catalog" style={navLinkStyle}>
                  <SearchOutlinedIcon fontSize="small" />
                  Catalog
                </NavLink>

                <Box
                  component="form"
                  role="search"
                  onSubmit={handleHeaderSearch}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    px: 1.25,
                    height: 36,
                    width: { md: 180, lg: 260 },
                    borderRadius: '8px',
                    border: 1,
                    borderColor: 'divider',
                    bgcolor: 'background.default',
                    '&:focus-within': { borderColor: 'primary.main' },
                  }}
                >
                  <SearchOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  <InputBase
                    value={headerQuery}
                    onChange={(event) => setHeaderQuery(event.target.value)}
                    placeholder="Search catalog…"
                    inputProps={{ 'aria-label': 'Search catalog' }}
                    sx={{ flex: 1, fontSize: '0.875rem' }}
                  />
                </Box>

                <Button
                  onClick={handleOpenOpsMenu}
                  endIcon={<KeyboardArrowDownIcon />}
                  sx={{
                    color: opsActive ? 'primary.light' : 'text.secondary',
                    bgcolor: opsActive ? (t) => alpha(t.palette.primary.main, 0.15) : 'transparent',
                    fontSize: '0.875rem',
                    fontWeight: opsActive ? 600 : 500,
                    px: 1.75,
                    py: 0.85,
                    borderRadius: '8px',
                    '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
                  }}
                >
                  Operations
                </Button>

                <Menu
                  id="menu-operations"
                  anchorEl={opsAnchorEl}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                  open={Boolean(opsAnchorEl)}
                  onClose={handleCloseOpsMenu}
                  sx={{
                    mt: 1,
                    '& .MuiPaper-root': {
                      borderRadius: 2,
                      minWidth: 180,
                    },
                  }}
                >
                  <MenuItem onClick={handleCloseOpsMenu} component={NavLink} to="/addsku" sx={{ py: 1.25, fontSize: '0.875rem', gap: 1.5 }}>
                    <AddCircleOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} /> Add SKU
                  </MenuItem>
                  <MenuItem onClick={handleCloseOpsMenu} component={NavLink} to="/dmdmfg" sx={{ py: 1.25, fontSize: '0.875rem', gap: 1.5 }}>
                    <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /> DMD MFGs
                  </MenuItem>
                  <MenuItem onClick={handleCloseOpsMenu} component={NavLink} to="/stmfg" sx={{ py: 1.25, fontSize: '0.875rem', gap: 1.5 }}>
                    <SettingsOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} /> Stevens MFGs
                  </MenuItem>
                </Menu>
              </Box>
            </Box>

            {/* Right Side Actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={handleDownloadStockFile}
                disabled={isGeneratingFile}
                startIcon={isGeneratingFile ? <CircularProgress size={16} color="inherit" /> : <CloudDownloadOutlinedIcon />}
                sx={{
                  fontWeight: 600,
                  fontSize: '0.825rem',
                  py: 0.8,
                  px: 1.75,
                  borderRadius: '8px',
                }}
              >
                {isGeneratingFile ? 'Generating...' : 'Download Stock File'}
              </Button>

              <Tooltip title="User settings">
                <IconButton onClick={handleOpenUserMenu} sx={{ p: 0.5 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      fontSize: '0.875rem',
                      fontWeight: 600,
                    }}
                  >
                    DN
                  </Avatar>
                </IconButton>
              </Tooltip>

              <Menu
                id="menu-user"
                anchorEl={anchorElUser}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                open={Boolean(anchorElUser)}
                onClose={handleCloseUserMenu}
                sx={{
                  mt: 1,
                  '& .MuiPaper-root': {
                    borderRadius: 2,
                    minWidth: 140,
                  },
                }}
              >
                <MenuItem onClick={() => handleUserAction('Account')} sx={{ py: 1, fontSize: '0.875rem' }}>
                  Account
                </MenuItem>
                <MenuItem onClick={() => handleUserAction('Logout')} sx={{ py: 1, fontSize: '0.875rem', color: 'error.main' }}>
                  Logout
                </MenuItem>
              </Menu>
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      <Snackbar
        open={downloadSuccess}
        autoHideDuration={4000}
        onClose={() => setDownloadSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity="success" onClose={() => setDownloadSuccess(false)} sx={{ borderRadius: 2 }}>
          Inventory stock file downloaded successfully
        </Alert>
      </Snackbar>

      <Dialog open={paymentErrorOpen} onClose={() => setPaymentErrorOpen(false)}>
        <DialogTitle sx={{ fontWeight: 600 }}>Payment Required</DialogTitle>
        <DialogContent>
          <Typography color="text.secondary">
            Inventory file generation is currently unavailable. Please verify account billing status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setPaymentErrorOpen(false)} variant="contained" color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Header;
